import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { X, Cookie, Shield, BarChart3, Settings } from "lucide-react";

const COOKIE_KEY = "hh_cookie_consent";

function loadConsent() {
  try {
    const raw = localStorage.getItem(COOKIE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveConsent(value) {
  try {
    localStorage.setItem(COOKIE_KEY, JSON.stringify({ ...value, date: new Date().toISOString() }));
  } catch {}
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [prefs, setPrefs] = useState({ analytics: false, marketing: false });

  useEffect(() => {
    const consent = loadConsent();
    if (!consent) {
      // Small delay so the page loads first
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    }
  }, []);

  function acceptAll() {
    saveConsent({ necessary: true, analytics: true, marketing: true });
    setVisible(false);
  }

  function rejectAll() {
    saveConsent({ necessary: true, analytics: false, marketing: false });
    setVisible(false);
  }

  function saveCustom() {
    saveConsent({ necessary: true, ...prefs });
    setVisible(false);
  }

  if (!visible) return null;

  return (
    <>
      {/* Overlay faint */}
      <div className="fixed inset-0 z-[998] bg-black/20 pointer-events-none" />

      {/* Banner */}
      <div
        className="fixed bottom-0 left-0 right-0 z-[999] p-4 md:p-6"
        role="dialog"
        aria-label="Gestion des cookies"
        aria-modal="true"
      >
        <div className="max-w-4xl mx-auto bg-[#1A120B] border border-[#C9922A]/30 rounded-2xl shadow-[0_-8px_60px_rgba(0,0,0,0.5)] overflow-hidden">

          {/* Gold top line */}
          <div className="h-[2px] bg-gradient-to-r from-transparent via-[#C9922A] to-transparent" />

          <div className="p-5 md:p-6">

            {/* Header */}
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-[#C9922A]/15 border border-[#C9922A]/30 flex items-center justify-center">
                <Cookie className="w-4 h-4 text-[#C9922A]" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-white font-semibold text-base leading-snug">
                  Respect de votre vie privée
                </h2>
                <p className="text-white/60 text-sm mt-1 leading-relaxed">
                  Nous utilisons des cookies pour améliorer votre expérience.
                  Vous pouvez accepter, refuser ou personnaliser vos préférences.{" "}
                  <Link
                    to="/politique-confidentialite"
                    className="text-[#C9922A] hover:text-[#E8C874] underline underline-offset-2 transition-colors"
                  >
                    En savoir plus
                  </Link>
                </p>
              </div>
              <button
                onClick={rejectAll}
                className="flex-shrink-0 p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/5 transition-colors"
                aria-label="Fermer et refuser"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Detailed preferences (toggle) */}
            {showDetails && (
              <div className="mb-4 space-y-2 bg-white/5 rounded-xl p-4 border border-white/10">
                {/* Necessary — always on */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <Shield className="w-4 h-4 text-[#C9922A] flex-shrink-0" />
                    <div>
                      <p className="text-white text-sm font-medium">Cookies nécessaires</p>
                      <p className="text-white/40 text-xs">Authentification, sécurité, préférences de session.</p>
                    </div>
                  </div>
                  <span className="text-xs text-[#C9922A] font-medium px-2 py-0.5 bg-[#C9922A]/10 rounded-full border border-[#C9922A]/20 flex-shrink-0">
                    Toujours actif
                  </span>
                </div>

                {/* Analytics */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <BarChart3 className="w-4 h-4 text-white/40 flex-shrink-0" />
                    <div>
                      <p className="text-white text-sm font-medium">Cookies analytiques</p>
                      <p className="text-white/40 text-xs">Mesure d'audience anonymisée pour améliorer le site.</p>
                    </div>
                  </div>
                  <button
                    role="switch"
                    aria-checked={prefs.analytics}
                    onClick={() => setPrefs(p => ({ ...p, analytics: !p.analytics }))}
                    className={`flex-shrink-0 relative w-10 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#C9922A]/50 ${
                      prefs.analytics ? "bg-[#C9922A]" : "bg-white/20"
                    }`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                      prefs.analytics ? "translate-x-5" : "translate-x-0"
                    }`} />
                  </button>
                </div>

                {/* Marketing */}
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <Settings className="w-4 h-4 text-white/40 flex-shrink-0" />
                    <div>
                      <p className="text-white text-sm font-medium">Cookies marketing</p>
                      <p className="text-white/40 text-xs">Personnalisation du contenu et publicités ciblées.</p>
                    </div>
                  </div>
                  <button
                    role="switch"
                    aria-checked={prefs.marketing}
                    onClick={() => setPrefs(p => ({ ...p, marketing: !p.marketing }))}
                    className={`flex-shrink-0 relative w-10 h-5 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#C9922A]/50 ${
                      prefs.marketing ? "bg-[#C9922A]" : "bg-white/20"
                    }`}
                  >
                    <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform duration-200 ${
                      prefs.marketing ? "translate-x-5" : "translate-x-0"
                    }`} />
                  </button>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
              {/* Personalise toggle */}
              <button
                onClick={() => setShowDetails(v => !v)}
                className="order-3 sm:order-1 sm:mr-auto flex items-center justify-center gap-1.5 px-4 py-2 text-xs font-medium text-white/50 hover:text-white/80 border border-white/10 hover:border-white/20 rounded-full transition-all duration-200"
              >
                <Settings className="w-3.5 h-3.5" />
                {showDetails ? "Masquer les options" : "Personnaliser"}
              </button>

              {/* Reject */}
              <button
                onClick={rejectAll}
                className="order-2 flex items-center justify-center px-5 py-2 text-xs font-semibold uppercase tracking-wider text-white/60 hover:text-white border border-white/15 hover:border-white/30 rounded-full transition-all duration-200"
              >
                Refuser
              </button>

              {/* Accept custom (only if details open) */}
              {showDetails && (
                <button
                  onClick={saveCustom}
                  className="order-1 sm:order-3 flex items-center justify-center px-5 py-2 text-xs font-semibold uppercase tracking-wider text-[#1A120B] bg-white/80 hover:bg-white rounded-full transition-all duration-200"
                >
                  Enregistrer mes choix
                </button>
              )}

              {/* Accept all */}
              <button
                onClick={acceptAll}
                className="order-1 sm:order-4 flex items-center justify-center gap-1.5 px-5 py-2 text-xs font-bold uppercase tracking-wider text-[#1A120B] bg-gradient-to-r from-[#C9922A] to-[#E8C874] hover:shadow-[0_4px_20px_rgba(201,146,42,0.4)] rounded-full transition-all duration-200"
              >
                Tout accepter
              </button>
            </div>
          </div>

          {/* Bottom gold line */}
          <div className="h-[2px] bg-gradient-to-r from-transparent via-[#C9922A]/40 to-transparent" />
        </div>
      </div>
    </>
  );
}

// Hook to read consent from anywhere in the app
export function useCookieConsent() {
  const consent = loadConsent();
  return {
    necessary: consent?.necessary ?? false,
    analytics: consent?.analytics ?? false,
    marketing: consent?.marketing ?? false,
    hasConsented: !!consent,
  };
}
